import { useCallback, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { purchasingAPI } from '../../lib/api';
import { useView } from '../../contexts/ViewContext';
import type { NCRFormData } from '../../components/quality/ncr-create/NCRDetailsForm';
import { displayPrice } from '../../lib/unitPrice';
import { toast } from 'sonner';

/**
 * The Create NCR screen's GRN lookup: finds the GRN by its exact number in the
 * view the user is in, and fills the order details from it and its PO line.
 * Split out of NCRCreate.tsx to keep that page under the 500-line limit;
 * behaviour unchanged.
 *
 * @example
 * const { doGRNLookup, lookingUpGRN } = useGrnLookup(setFormData, setGrnLinked, priceDecimals);
 *
 * @param setFormData - the Create NCR form state setter
 * @param setGrnLinked - marks the form as linked to a GRN once one is found
 * @param priceDecimals - the user's unit price precision
 * @returns the lookup, and whether one is running
 */
export function useGrnLookup(
  setFormData: Dispatch<SetStateAction<NCRFormData>>,
  setGrnLinked: (linked: boolean) => void,
  priceDecimals: number,
): {
  doGRNLookup: (grnNumber: string, suppliersList: Array<{ id: number; name: string }>) => Promise<boolean>;
  lookingUpGRN: boolean;
} {
  // Group/company/site the user is currently viewing — the GRN lookup is
  // scoped to it so a number that exists on another site is not linkable here.
  const { getViewQueryParams } = useView();
  const [lookingUpGRN, setLookingUpGRN] = useState(false);

  const doGRNLookup = useCallback(async (grnNumber: string, suppliersList: Array<{ id: number; name: string }>) => {
    try {
      setLookingUpGRN(true);
      // Exact match on the GRN number, and scoped to the site the user is
      // viewing. `search=` hits grn_number, part number, PO number AND batch
      // number by substring, so "96" pulled back unrelated GRNs — and the old
      // `|| grns[0]` fallback then silently linked the first of them and
      // reported success. An NCR pointed at the wrong GRN is worse than one
      // the user has to enter by hand, so a near miss is now no match.
      const response = await purchasingAPI.getGRNs({
        grn_number: grnNumber,
        page_size: 5,
        all_suppliers: true,
        ...getViewQueryParams(),
      });
      const grns = response.data.results;
      const grn = grns?.find(g => g.grn_number === grnNumber);

      if (!grn) {
        toast.error(`No GRN found with the number "${grnNumber}" in the current view.`);
        return false;
      }
      // Cast to access all fields returned by API (some not in TS type)
      const grnData = grn as unknown as Record<string, unknown>;

      const updates: Partial<NCRFormData> = {
        grn: grn.id,
        grn_number: grn.grn_number || grnNumber,
      };

      // CR-7347: the receiving warehouse. The GRN carries its own once the
      // CR-7348 backfill has run; where it does not, the PO line lookup below
      // supplies it, and failing both the server falls back to the same chain
      // on create — so the picker is only ever left for the user when nothing
      // in the order knows the answer.
      const grnWarehouse = grnData['delivery_warehouse'];
      if (typeof grnWarehouse === 'number') updates.warehouse = grnWarehouse;

      // Populate from top-level GRN fields (API returns these directly)
      if (grn.po_number) updates.po_number = grn.po_number;
      if (grn.line_number) updates.po_line_number = String(grn.line_number);
      if (grn.part_number) updates.part_number = grn.part_number;
      if (grnData['description']) updates.description = String(grnData['description']);
      // CR-7290(b): a `grn.part_description` fallback used to live here. It was
      // always a NO-OP — that key and `description` were the same column, so
      // when one was empty so was the other. This diff makes part_description
      // the PART's catalogue text, which would have turned a dead line into a
      // live one and quietly changed what a new NCR PERSISTS. Removed rather
      // than guarded: removing it is exactly the behaviour this form has always
      // had. Whether an NCR should describe the part or the order line is a
      // product question, and not this ticket's to answer.
      // NCR.unit_price is a 2dp field. PO/GRN prices carry 3, so copying one
      // straight across produced "15.000", which the serializer rejects with
      // "Ensure that there are no more than 2 decimal places" — the form
      // looked filled in correctly and simply refused to save.
      if (grn.unit_price) updates.unit_price = displayPrice(grn.unit_price, priceDecimals);
      if (grn.batch_number) updates.batch_number = grn.batch_number;

      // Set po_line FK (API may return as po_line or po_line_id)
      const poLineId = grn.po_line || (grnData['po_line_id'] as number | undefined);
      if (poLineId) {
        updates.po_line = poLineId;
        // Try to fetch PO line for additional details (supplier, etc.)
        try {
          const lineResp = await purchasingAPI.getLine(poLineId);
          const line = lineResp.data;
          if (line.po_number && !updates.po_number) updates.po_number = line.po_number;
          if (line.line_number && !updates.po_line_number) updates.po_line_number = String(line.line_number);
          if (line.part_number && !updates.part_number) updates.part_number = line.part_number;
          if (line.description && !updates.description) updates.description = line.description;
          if (line.unit_price && !updates.unit_price) updates.unit_price = displayPrice(line.unit_price, priceDecimals);
          if (line.supplier_id) updates.supplier = line.supplier_id;
          const lineWarehouse = (line as unknown as Record<string, unknown>)['delivery_warehouse'];
          if (updates.warehouse == null && typeof lineWarehouse === 'number') {
            updates.warehouse = lineWarehouse;
          }
        } catch {
          // PO line lookup failed, use what we have from GRN
        }
      }

      // Fallback: resolve supplier from name if not set from PO line
      if (grn.supplier_name && !updates.supplier) {
        const match = suppliersList.find(s => s.name === grn.supplier_name);
        if (match) updates.supplier = match.id;
      }

      setFormData(prev => ({ ...prev, ...updates }));
      setGrnLinked(true);
      toast.success('GRN data loaded - supplier, PO, and part details populated');
      return true;
    } catch (error: unknown) {
      console.error('Error looking up GRN:', error);
      toast.error('Failed to look up GRN');
      return false;
    } finally {
      setLookingUpGRN(false);
    }
    // Both are read inside: an empty dep list would pin the lookup to the
    // view context and precision as they were on first render, so switching
    // site and looking up again would silently query the old scope.
  }, [getViewQueryParams, priceDecimals, setFormData, setGrnLinked]);

  return { doGRNLookup, lookingUpGRN };
}
